import { LocalDate } from '@js-joda/core';
import { useCallback, useMemo } from 'react';

import { usePaidBreakExceptionStore } from '#/shared/stores/paidBreakExceptionStore';
import { formatDurationToHours, parseHoursToDuration } from '#/shared/utils/dateTimeHelper';

import { ShiftDataEntry } from '../types/types';
import { PaidBreakExceptionFormField } from './PaidBreakExceptionFormField';
import { TimesheetShiftEntryRow } from './TimesheetShiftEntryRow';

type TimesheetShiftEntryOrdinaryRowProps = {
  shiftDate: LocalDate;
  timeEntry: ShiftDataEntry;
  isLastShift?: boolean;
};

export function TimesheetShiftEntryOrdinaryRow({
  shiftDate,
  timeEntry,
  isLastShift = false,
}: Readonly<TimesheetShiftEntryOrdinaryRowProps>) {
  const paidBreakException = usePaidBreakExceptionStore(state => state.getException(shiftDate));

  const paidBreakExceptionIsAccepted = useMemo(() => {
    return paidBreakException?.approverDecision === 'accepted';
  }, [paidBreakException]);

  const getDurations = useCallback(() => {
    if (paidBreakExceptionIsAccepted && paidBreakException?.break) {
      const breakDuration = paidBreakException.break;
      const totalDuration = parseHoursToDuration(timeEntry.totalDuration).plus(breakDuration);
      const breakDurationResult = parseHoursToDuration(timeEntry.breakDuration).minus(breakDuration);

      return {
        totalDuration: formatDurationToHours(totalDuration),
        breakDuration: formatDurationToHours(breakDurationResult),
      };
    }
    return {
      totalDuration: timeEntry.totalDuration,
      breakDuration: timeEntry.breakDuration,
    };
  }, [timeEntry.totalDuration, timeEntry.breakDuration, paidBreakException, paidBreakExceptionIsAccepted]);

  const updatedTimeEntry = useMemo(() => {
    const { totalDuration, breakDuration } = getDurations();

    return {
      ...timeEntry,
      totalDuration,
      breakDuration,
    };
  }, [timeEntry, getDurations]);

  return (
    <>
      <TimesheetShiftEntryRow timeEntry={updatedTimeEntry} isLastShift={paidBreakException ? true : isLastShift} />
      {paidBreakException && <PaidBreakExceptionFormField shiftDate={shiftDate} isLastShift={isLastShift} />}
    </>
  );
}
